import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

const PushNotificationManager = () => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check current state on mount
  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    setPermission(Notification.permission);

    // Don't show banner if already granted or denied, or recently dismissed
    const dismissedAt = localStorage.getItem('sb_push_dismissed');
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 7 * 24 * 60 * 60 * 1000) {
      setDismissed(true);
      return;
    }

    // Check if already subscribed
    navigator.serviceWorker.ready.then(async (registration) => {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        setIsSubscribed(true);
      } else if (Notification.permission === 'default') {
        // Show banner after 5 seconds (don't nag immediately)
        setTimeout(() => setShowBanner(true), 5000);
      }
    });
  }, []);

  const subscribe = useCallback(async () => {
    if (!VAPID_KEY) {
      console.error('VAPID key not configured');
      return;
    }

    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        toast.error('Notification permission denied');
        setLoading(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });

      // Send subscription to backend
      await api.post('/api/push/subscribe', {
        subscription: subscription.toJSON(),
      });

      setIsSubscribed(true);
      setShowBanner(false);
      toast.success('Notifications enabled! You\'ll get alerts for deliveries, wallet, and KYC updates.');
    } catch (err) {
      console.error('Push subscription error:', err);
      toast.error('Failed to enable notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.post('/api/push/unsubscribe', {
          endpoint: subscription.endpoint,
        });
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
      toast.success('Notifications disabled');
    } catch (err) {
      console.error('Unsubscribe error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const dismissBanner = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('sb_push_dismissed', Date.now().toString());
  };

  // Don't render if browser doesn't support push
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }

  // Notification enable/disable toggle for settings page
  if (permission === 'granted') {
    return (
      <div data-testid="push-notification-toggle" className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="w-5 h-5 text-emerald-400" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-400" />
          )}
          <div>
            <p className="text-sm font-medium text-white">Push Notifications</p>
            <p className="text-xs text-slate-400">
              {isSubscribed ? 'Receiving delivery, wallet & KYC alerts' : 'Notifications are off'}
            </p>
          </div>
        </div>
        <Button
          data-testid="push-toggle-btn"
          size="sm"
          variant={isSubscribed ? 'outline' : 'default'}
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={loading}
          className={isSubscribed ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'bg-sky-600 hover:bg-sky-700'}
        >
          {loading ? 'Processing...' : isSubscribed ? 'Turn Off' : 'Turn On'}
        </Button>
      </div>
    );
  }

  // Permission prompt banner (shows once after 5s for new users)
  if (showBanner && !dismissed && permission === 'default') {
    return (
      <div
        data-testid="push-notification-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 bg-slate-800 border border-sky-500/30 rounded-xl p-4 shadow-2xl"
        style={{ animation: 'slideUp 0.3s ease-out' }}
      >
        <button
          onClick={dismissBanner}
          className="absolute top-2 right-2 text-slate-400 hover:text-white"
          data-testid="push-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-sky-500/20">
            <Bell className="w-5 h-5 text-sky-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Stay Updated</p>
            <p className="text-xs text-slate-400 mt-1">
              Get instant alerts for deliveries, wallet transactions, and KYC status changes.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                data-testid="push-enable-btn"
                size="sm"
                onClick={subscribe}
                disabled={loading}
                className="bg-sky-600 hover:bg-sky-700 text-white text-xs"
              >
                {loading ? 'Enabling...' : 'Enable Notifications'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismissBanner}
                className="text-slate-400 hover:text-white text-xs"
              >
                Not now
              </Button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default PushNotificationManager;
